/*
   Data-fetch layer for the /admin/clients page.

   Split into two queries so pagination + search + sort of the engagements list
   round-trips only the current page, while the small "who can I add as a member"
   user roster is fetched once and reused across pages.

   Fields the wire does not carry today — flagged inline:
     - member.lastSeen  → accepted_at fallback (no true login-history endpoint yet)
     - openReviewCount  → 0   (no per-status breakdown on ClientOut)
*/

import {
  keepPreviousData,
  queryOptions,
  useQuery,
  type QueryClient,
  type UseQueryResult,
} from "@tanstack/react-query"

import { http } from "@/lib/http"
import { fromPageWire, toQueryParams, type Page, type PageWire } from "@/lib/pagination"
import { qk } from "@/lib/query-keys"
import { Role } from "@/lib/roles"
import type { TableQueryParams } from "@/lib/table"

import type {
  ClientId,
  ClientMember,
  ClientRow,
  ClientUserOption,
  UserId,
} from "./clients-data"

// ── Wire types (mirror routes_clients.py + routes_users.py) ────────────────

interface ClientMemberWire {
  user_id: number
  is_primary: boolean
  created_at: string
}

interface ClientOutWire {
  id: number
  name: string
  primary_client_contact_id: number | null
  notes: string | null
  is_active: boolean
  created_at: string
  members: ClientMemberWire[]
  review_count: number
}

interface UserOutWire {
  id: number
  email: string
  full_name: string | null
  role: string
  is_active: boolean
  created_at: string
  invited_at: string | null
  accepted_at: string | null
}

// ── Adapters ─────────────────────────────────────────────────────────────

function makeMember(
  m: ClientMemberWire,
  usersById: Map<number, UserOutWire>,
): ClientMember | null {
  const u = usersById.get(m.user_id)
  if (!u) return null
  // Backend has no login-history yet; accept-invite is the earliest sign-in we can prove, so use
  // it as the fallback rather than showing "Never signed in" for someone who obviously has.
  return {
    userId: String(u.id) as UserId,
    name: u.full_name ?? u.email,
    email: u.email,
    isPrimary: m.is_primary,
    lastSeen: u.accepted_at,
  }
}

function toClientRow(c: ClientOutWire, usersById: Map<number, UserOutWire>): ClientRow {
  const members = c.members
    .map((m) => makeMember(m, usersById))
    .filter((m): m is ClientMember => m !== null)
  return {
    id: String(c.id) as ClientId,
    name: c.name,
    primaryContactId:
      c.primary_client_contact_id != null
        ? (String(c.primary_client_contact_id) as UserId)
        : null,
    notes: c.notes,
    isActive: c.is_active,
    createdAt: c.created_at,
    members,
    reviewCount: c.review_count,
    openReviewCount: 0,
  }
}

function toClientUserOption(u: UserOutWire): ClientUserOption {
  return {
    id: String(u.id) as UserId,
    name: u.full_name ?? u.email,
    email: u.email,
  }
}

// ── The client-role user roster (small, static-ish). ──────────────────────
//
// One fetch of the raw wire, two derived shapes via `select` so the "add member"
// combobox (sorted ClientUserOption[]) and the row adapter (Map<id, UserOut>) don't
// each cost a round trip.

async function fetchClientUsersRaw(signal: AbortSignal): Promise<UserOutWire[]> {
  // /users returns Page[UserOut]. Cap page_size at 100 for the picker — a client-role
  // roster larger than that would need its own async combobox rather than one dropdown.
  const wire = await http.get<PageWire<UserOutWire>>("/api/v1/users", {
    params: { role: Role.CLIENT, page_size: 100 },
    signal,
  })
  return wire.items
}

const clientUsersRawQuery = () =>
  queryOptions({
    queryKey: [...qk.users.all, "list", { role: Role.CLIENT }] as const,
    queryFn: ({ signal }) => fetchClientUsersRaw(signal),
    staleTime: 60_000,
  })

export const clientUsersQuery = () =>
  queryOptions({
    ...clientUsersRawQuery(),
    select: (rows: UserOutWire[]) =>
      rows
        .filter((u) => u.is_active)
        .sort((a, b) => (a.full_name ?? a.email).localeCompare(b.full_name ?? b.email))
        .map(toClientUserOption),
  })

export function useClientUsersQuery(): UseQueryResult<ClientUserOption[]> {
  return useQuery(clientUsersQuery())
}

// ── The paginated engagement list. ────────────────────────────────────────

export interface ClientsPageParams {
  page: number
  pageSize: number
  search: string
  sort: { field: string; dir: "asc" | "desc" } | null
}

async function fetchClientsPage(
  qc: QueryClient,
  params: ClientsPageParams,
  signal: AbortSignal,
): Promise<Page<ClientRow>> {
  // Warm the users cache once, then build the id → user map inside the queryFn —
  // avoids a per-render Map reference change that would infinite-loop useTable's
  // fetchData dep if we passed the Map in from a `select`.
  const users = await qc.ensureQueryData(clientUsersRawQuery())
  const usersById = new Map(users.map((u) => [u.id, u]))
  const wire = await http.get<PageWire<ClientOutWire>>("/api/v1/clients", {
    params: toQueryParams(
      {
        page: params.page,
        pageSize: params.pageSize,
        sort: params.sort,
        filters: {},
        search: params.search,
      } satisfies TableQueryParams,
      { include_inactive: true },
    ),
    signal,
  })
  return fromPageWire(wire, (c) => toClientRow(c, usersById))
}

export const clientsQuery = (qc: QueryClient, params: ClientsPageParams) =>
  queryOptions({
    queryKey: qk.clients.list(params),
    queryFn: ({ signal }) => fetchClientsPage(qc, params, signal),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })
